import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaRadiation, 
  FaClock, 
  FaUsers, 
  FaStar,
  FaCheckCircle,
  FaArrowRight,
  FaBookOpen,
  FaCertificate,
  FaCalendarAlt,
  FaMicroscope,
  FaFlask,
  FaChartLine,
  FaVideo
} from 'react-icons/fa';

const PageContainer = styled.div`
  margin-top: 200px;
  min-height: calc(100vh - 210px);
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #0a4d68 0%, #088395 50%, #0a2b3c 100%);
  color: white;
  padding: 4rem 5%;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  
  span {
    color: #ffd966;
    display: block;
    font-size: 2rem;
    font-weight: 300;
    margin-top: 0.5rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 2rem;
  opacity: 0.95;
  position: relative;
  z-index: 2;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 5%;
`;



const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  .value {
    font-size: 2.2rem;
    font-weight: 700;
    color: #0a4d68;
    margin-bottom: 0.3rem;
  }
  
  .label {
    color: #5a6b7a;
    font-size: 0.9rem;
    text-transform: uppercase;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: #0a2b3c;
  margin-bottom: 2rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 4px;
    background: #ffd966;
    border-radius: 2px;
  }
`;

const Grid2Col = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContentBlock = styled.div`
  p {
    color: #4a5f73;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
  }
`;

const ImageWrapper = styled.div`
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.5s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }
`;

const Grid4Col = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin: 3rem 0;
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ModalityCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  text-align: center;
  
  svg {
    font-size: 2.5rem;
    color: #ffd966;
    margin-bottom: 1rem;
  }
  
  h4 {
    color: #0a4d68;
    margin-bottom: 0.5rem;
    font-size: 1rem;
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
`;

const ListItem = styled.li`
  margin-bottom: 0.8rem;
  color: #4a5f73;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  svg {
    color: #ffd966;
    font-size: 1rem;
  }
`;

const CEUInfo = styled.div`
  background: linear-gradient(135deg, #0a4d68, #088395);
  color: white;
  border-radius: 20px;
  padding: 2rem;
  margin: 3rem 0;
  
  .ceus {
    display: flex;
    justify-content: space-around;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 1rem;
  }
  
  .ceu-item {
    text-align: center;
    background: rgba(255, 255, 255, 0.1);
    padding: 1rem;
    border-radius: 10px;
    min-width: 100px;
    
    .number {
      font-size: 2rem;
      font-weight: 700;
      color: #ffd966;
    }
    
    .org {
      font-size: 0.9rem;
    }
  }
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #0a4d68, #088395);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  color: white;
  margin-top: 4rem;
  
  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    opacity: 0.9;
  }
`;

const CTAButton = styled(Link)`
  background: #ffd966;
  color: #0a4d68;
  border: none;
  padding: 1rem 3rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const RadiationOncology = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  

  const modalities = [
    { name: "IMRT", desc: "Intensity-Modulated Radiation Therapy" },
    { name: "SBRT", desc: "Stereotactic Body Radiation Therapy" },
    { name: "Brachytherapy", desc: "Internal Radiation Therapy" },
    { name: "Proton Therapy", desc: "Proton Beam Therapy" },
    { name: "SRS", desc: "Stereotactic Radiosurgery" },
    { name: "3D-CRT", desc: "3D Conformal Radiation Therapy" },
    { name: "IGRT", desc: "Image-Guided Radiation Therapy" },
    { name: "EBRT", desc: "External Beam Radiation Therapy" }
  ];

  const ceus = [
    { number: "32", org: "AHIMA" },
    { number: "17.5", org: "AAPC" },
    { number: "14.75", org: "ARRT" }
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>
          Radiation Oncology Coding
          <span>Complete Radiation Therapy Coding</span>
        </HeroTitle>
        <HeroDescription>
          Complete Radiation Oncology Coding provides essential training for coding and billing professionals 
          working in physician practices, non-hospital treatment centers, and hospital outpatient settings. [citation:6]
        </HeroDescription>
      </HeroSection>

      <Container>
        

        <Grid2Col>
          <ContentBlock>
            <SectionTitle>Program Overview</SectionTitle>
            <p>
              Complete Radiation Oncology Coding includes courses on ICD-10-CM diagnosis coding, CPT 
              procedure coding, as well as specific guidance on the coding of the most common radiation 
              modalities. Each course explains procedure techniques, coding guidelines, modifier use, 
              coverage issues, and reimbursement considerations. [citation:6]
            </p>
            <p>
              The courses contain examples, case studies and multiple-choice exercises to improve 
              comprehension and retention. At the end of each course there is a multiple-choice 
              assessment to test the learner's knowledge of the material. [citation:6]
            </p>
          </ContentBlock>
          
          <ImageWrapper>
            <img 
              src="https://images.unsplash.com/photo-1581093458791-9a3c3a2c3e4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
              alt="Radiation Oncology"
            />
          </ImageWrapper>
        </Grid2Col>

        <SectionTitle>Radiation Modalities Covered</SectionTitle>
        <Grid4Col>
          {modalities.map((modality, index) => (
            <ModalityCard key={index}>
              <FaRadiation />
              <h4>{modality.name}</h4>
              <p style={{ fontSize: '0.8rem', color: '#5a6b7a' }}>{modality.desc}</p>
            </ModalityCard>
          ))}
        </Grid4Col>

        <CEUInfo>
          <h3 style={{ textAlign: 'center', color: 'white' }}>Continuing Education Units</h3>
          <div className="ceus">
            {ceus.map((ceu, index) => (
              <div className="ceu-item" key={index}>
                <div className="number">{ceu.number}</div>
                <div className="org">{ceu.org}</div>
              </div>
            ))}
          </div>
        </CEUInfo>

        <CTASection>
          <h3>Ready to Master Radiation Oncology Coding?</h3>
          <p>Join our next batch and become an expert in radiation therapy coding</p>
          <CTAButton to="/register">
            Enroll Now <FaArrowRight />
          </CTAButton>
        </CTASection>
      </Container>
    </PageContainer>
  );
};

export default RadiationOncology;